/* eslint-disable max-lines */
import {
  IEsimCompetitorComparisonReport,
  IEsimCompetitorPlan,
  IEsimOwnPlan
} from "../../interfaces/esim/EsimMargin.types";
import { packageLabel } from "./margin";

// The sizes a customer shops for; each one asks "what does at least this much data cost".
export const CURVE_SIZES_GB = [1, 2, 3, 5, 10, 15, 20, 30, 50, 100];

// Our own series key, as the legend and the table header show it.
export const OURS = "Ne";

// A fixed slot per seller, so a competitor keeps its colour whichever country is open and however
// many sellers that country has. The slots are the categorical order of the data-viz palette.
const SELLER_ORDER = [OURS, "Airalo", "Saily", "Holafly", "Kudoesim", "Viaesim", "99esim"];
const SLOT_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#6250d6", "#e34948"];
const UNKNOWN_SELLER_COLOR = "#898781";

export const colorOf = (seller: string): string => {
  const slot = SELLER_ORDER.indexOf(seller);
  return slot >= 0 ? SLOT_COLORS[slot] : UNKNOWN_SELLER_COLOR;
};

export interface CurveOffer {
  price: number;
  // EUR when the price was converted, else the plan's own currency, taken at face value.
  currency: string;
  plan: string;
  dataGb: number;
  days: number | null;
  scope: string | null;
}

export interface CurvePoint {
  sizeGb: number;
  // One entry per seller of the curve; null when the seller sells nothing this large.
  offers: Record<string, CurveOffer | null>;
  cheapest: string | null;
  // Our price against the cheapest competitor's at this size, in percent; positive when we are dearer.
  gapPercent: number | null;
  // The currency of the competitor figure behind the gap; not EUR means the gap is at face value.
  gapCurrency: string | null;
}

export interface UnlimitedNote {
  seller: string;
  fromPerDay: number;
  currency: string;
}

export interface PriceCurve {
  // Us first, then the competitors with at least one sized, priced plan, in slot order.
  sellers: string[];
  // The currency each seller's figures are in.
  sellerCurrency: Record<string, string>;
  points: CurvePoint[];
  // Sellers with unlimited plans only: they never sit on a GB axis, so they are a note.
  unlimitedOnly: UnlimitedNote[];
}

interface SizedPlan {
  seller: string;
  plan: string;
  dataGb: number;
  price: number;
  currency: string;
  days: number | null;
  scope: string | null;
}

const figureOf = (p: IEsimCompetitorPlan) => p.priceEur ?? p.price;
const currencyOf = (p: IEsimCompetitorPlan) => (p.priceEur !== null ? "EUR" : p.currency || "EUR");

// A free package is a gift, not a price, on either side.
const ourSized = (plans: IEsimOwnPlan[]): SizedPlan[] =>
  plans
    .filter((p) => !p.isUnlimited && p.allowanceGb > 0 && p.price > 0)
    .map((p) => ({
      seller: OURS,
      plan: packageLabel(p.packageName) ?? `#${p.packageTemplateId}`,
      dataGb: p.allowanceGb,
      price: p.price,
      currency: "EUR",
      days: p.validityDays,
      scope: null
    }));

const theirSized = (plans: IEsimCompetitorPlan[]): SizedPlan[] =>
  plans
    .filter((p) => !p.isUnlimited && p.dataGb !== null && p.dataGb > 0 && figureOf(p) > 0)
    .map((p) => ({
      seller: p.competitor,
      plan: p.planName,
      dataGb: p.dataGb as number,
      price: figureOf(p),
      currency: currencyOf(p),
      days: p.validityDays,
      scope: p.scope
    }));

const cheapestAtLeast = (plans: SizedPlan[], sizeGb: number): CurveOffer | null => {
  let best: SizedPlan | null = null;
  for (const p of plans) {
    if (p.dataGb >= sizeGb && (best === null || p.price < best.price)) best = p;
  }
  if (best === null) return null;
  const { price, currency, plan, dataGb, days, scope } = best;
  return { price, currency, plan, dataGb, days, scope };
};

const slotOf = (seller: string) => {
  const slot = SELLER_ORDER.indexOf(seller);
  return slot >= 0 ? slot : SELLER_ORDER.length;
};

// Sellers in their fixed order; the like-for-like table sorts its columns with it too.
export const bySlot = (a: string, b: string) => slotOf(a) - slotOf(b) || a.localeCompare(b);

const unlimitedNote = (seller: string, plans: IEsimCompetitorPlan[]): UnlimitedNote | null => {
  const unlimited = plans.filter((p) => p.isUnlimited && p.validityDays !== null && p.validityDays > 0);
  if (unlimited.length === 0) return null;
  const cheapest = unlimited.reduce((best, p) =>
    figureOf(p) / (p.validityDays as number) < figureOf(best) / (best.validityDays as number) ? p : best
  );
  return {
    seller,
    fromPerDay: figureOf(cheapest) / (cheapest.validityDays as number),
    currency: currencyOf(cheapest)
  };
};

const priceOf = (offers: Record<string, CurveOffer | null>, seller: string) =>
  (offers[seller] as CurveOffer).price;

const pointAt = (sizeGb: number, sellers: string[], sized: SizedPlan[]): CurvePoint => {
  const offers: Record<string, CurveOffer | null> = {};
  sellers.forEach((seller) => {
    offers[seller] = cheapestAtLeast(sized.filter((p) => p.seller === seller), sizeGb);
  });
  const priced = sellers.filter((s) => offers[s] !== null);
  const cheapest = priced.length
    ? priced.reduce((best, s) => (priceOf(offers, s) < priceOf(offers, best) ? s : best))
    : null;
  const ours = offers[OURS];
  const rivals = priced.filter((s) => s !== OURS);
  const cheapestRival = rivals.length
    ? rivals.reduce((best, s) => (priceOf(offers, s) < priceOf(offers, best) ? s : best))
    : null;
  const rivalOffer = cheapestRival ? (offers[cheapestRival] as CurveOffer) : null;
  const gapPercent = ours && rivalOffer ? ((ours.price - rivalOffer.price) / rivalOffer.price) * 100 : null;
  const gapCurrency = gapPercent === null ? null : (rivalOffer as CurveOffer).currency;
  return { sizeGb, offers, cheapest, gapPercent, gapCurrency };
};

export const buildPriceCurve = (detail: IEsimCompetitorComparisonReport): PriceCurve => {
  const sized = [...ourSized(detail.ourPlans), ...theirSized(detail.plans)];
  const competitors = Array.from(new Set(detail.plans.map((p) => p.competitor))).sort(bySlot);
  const sellers = [OURS, ...competitors.filter((c) => sized.some((p) => p.seller === c))];
  const sellerCurrency: Record<string, string> = {};
  sellers.forEach((seller) => {
    sellerCurrency[seller] = sized.find((p) => p.seller === seller)?.currency ?? "EUR";
  });
  const points = CURVE_SIZES_GB.map((sizeGb) => pointAt(sizeGb, sellers, sized));

  const unlimitedOnly = competitors
    .filter((c) => !sellers.includes(c))
    .map((c) => unlimitedNote(c, detail.plans.filter((p) => p.competitor === c)))
    .filter((note): note is UnlimitedNote => note !== null);

  return { sellers, sellerCurrency, points, unlimitedOnly };
};
